/**
 * What a match, a bet line and a placed bet are on this side of the API — and, more importantly,
 * the derived signals.
 *
 * **The signal rules are the product.** Each was learned by being wrong, and each is a test case in
 * `src/stats/__tests__` or in `models.test.ts` rather than a comment someone can quietly relax:
 *
 * - `isGoodBet = (suggested || hasEdge) && !historyContradicts`, and the **bolding keys off that**,
 *   never off `suggested` alone. The model sets `recommend_*` about once in 45 bets, so bolding on
 *   it alone left real edges unhighlighted.
 * - `hasEdge` = AI% − market% ≥ 5 points. **This is the only signal that makes money**: a bet that
 *   is likely but fairly priced returns nothing over time, and less than nothing after fees.
 * - **A high win % on its own is deliberately never enough.** A bet the model rates below the
 *   market price is a bad bet however likely it is to land.
 * - `historyContradicts` suppresses a claimed edge the league's own history flatly denies.
 * - **Conviction is not value.** `convictionEdge` (AI% − league base rate) answers "is this match
 *   unusual?"; `edge` answers "is it cheap?". Only the second is a reason to bet.
 */

/** Percentage-point gap of AI over market at or above which a bet is flagged as edge. */
export const EDGE_THRESHOLD = 5;

/** Points above the league base rate at which the model counts as genuinely convinced. */
export const CONVICTION_THRESHOLD = 5;

/** 90' + half time + stoppage. A match is "in play" for this long after kickoff. */
export const LIVE_WINDOW_MS = 3 * 60 * 60 * 1000;

/** The seven lines, in the order they render. BTTS exists on ~one tracked league; the result
 *  markets exist on all of them, which is what makes the rest of the board usable at all. */
export const BET_LINES = [
  'btts',
  'bttsNo',
  'under35',
  'over25',
  'homeWin',
  'draw',
  'awayWin',
] as const;
export type BetKey = (typeof BET_LINES)[number];

export const BET_NAMES: Record<BetKey, string> = {
  btts: 'BTTS',
  bttsNo: 'BTTS No',
  under35: 'Under 3.5 total goals',
  over25: 'Over 2.5 total goals',
  homeWin: 'Home win',
  draw: 'Draw',
  awayWin: 'Away win',
};

export const BET_SHORT: Record<BetKey, string> = {
  btts: 'BTTS',
  bttsNo: 'BTTS No',
  under35: 'U 3.5',
  over25: 'O 2.5',
  homeWin: 'Home',
  draw: 'Draw',
  awayWin: 'Away',
};

export interface BetLine {
  key: BetKey;
  name: string;
  /** The model's predicted win %, 0-100. Null before a prediction has been run. */
  aiPercent: number | null;
  /** Market-implied win %, 0-100, from the venue the bet would actually be placed on. */
  marketPercent: number | null;
  /** How often this bet has landed in this league — a model-free reference point. */
  leaguePercent: number | null;
  /** The history-only Poisson estimate, independent of both the LLM and the market. */
  profilePercent: number | null;
  /** The propensity model flagged this fixture for this line. */
  profileFlagged: boolean;
  /** The model's own holistic recommendation. Rare: ~once in 45 bets. */
  suggested: boolean;
  /** Polymarket US market slug. Null means the venue does not list it, so it cannot be bet. */
  usMarketSlug: string | null;
}

/** AI probability minus market-implied probability, in points. Null when either is unknown. */
export const edge = (b: BetLine): number | null =>
  b.aiPercent !== null && b.marketPercent !== null ? b.aiPercent - b.marketPercent : null;

export const hasEdge = (b: BetLine): boolean => {
  const e = edge(b);
  return e !== null && e >= EDGE_THRESHOLD;
};

/**
 * League base rate minus the market price: what the bet is worth on history alone, with no model
 * involved. If the market already prices the historical rate there is nothing to win, however
 * likely the outcome.
 */
export const baseRateEdge = (b: BetLine): number | null =>
  b.leaguePercent !== null && b.marketPercent !== null ? b.leaguePercent - b.marketPercent : null;

/**
 * The model claims an edge the league's own history flatly contradicts — above the market on a bet
 * that has landed LESS often than the price implies. Far more likely model error than value.
 */
export const historyContradicts = (b: BetLine): boolean => {
  const r = baseRateEdge(b);
  return r !== null && r < -EDGE_THRESHOLD;
};

/** VALUE. What the bold styling keys off — never `suggested` alone, never a high % alone. */
export const isGoodBet = (b: BetLine): boolean =>
  (b.suggested || hasEdge(b)) && !historyContradicts(b);

/** CONVICTION: is this match unusual? Market-independent, and not a reason to bet on its own. */
export const convictionEdge = (b: BetLine): number | null =>
  b.aiPercent !== null && b.leaguePercent !== null ? b.aiPercent - b.leaguePercent : null;

export const isHighConviction = (b: BetLine): boolean =>
  (convictionEdge(b) ?? -Infinity) >= CONVICTION_THRESHOLD;

/** Worth the eye: mispriced, an unusually strong spot, or flagged by the profile model. */
export const isNotable = (b: BetLine): boolean =>
  isGoodBet(b) || isHighConviction(b) || (b.profileFlagged && b.aiPercent !== null);

/** The propensity flag is only shown once a prediction exists — see `showProfileFlag` below. */
export const showProfileFlag = (b: BetLine): boolean => b.profileFlagged && b.aiPercent !== null;

export const canPlaceBet = (b: BetLine): boolean => !!b.usMarketSlug;

export interface Prediction {
  expectedTotalGoals: number;
  expectedHomeGoals: number;
  expectedAwayGoals: number;
  topScores: string[];
  explanation: string;
  homeAttack: string | null;
  awayAttack: string | null;
  homeDefense: string | null;
  awayDefense: string | null;
  keyAttackerMissing: boolean;
  keyDefenderMissing: boolean;
  openGameExpected: boolean;
  ranAtUtc: string | null;
}

/** Queued and pending are normal states: the PC executor may be asleep and the app says so. */
export type JobStatus = 'queued' | 'running' | 'done' | 'failed' | null;

export interface Match {
  eventId: string;
  slug: string;
  league: string;
  homeTeam: string;
  awayTeam: string;
  kickoffUtc: string;
  liquidity: number;
  volume: number;
  marketCount: number;
  bets: Record<BetKey, BetLine>;
  prediction: Prediction | null;
  jobStatus: JobStatus;
  jobError: string | null;
  /** Which per-league model produced this card's signals, e.g. "MLS · 810 results". */
  modelInfo: string | null;
}

export const hasPrediction = (m: Match) => m.prediction !== null;

export const kickoffMs = (m: { kickoffUtc: string }) => {
  const t = Date.parse(m.kickoffUtc);
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
};

/** Kicked off and past the live window — finished, and it belongs on Complete. */
export const isComplete = (m: { kickoffUtc: string }, now = Date.now()) => {
  const t = kickoffMs(m);
  return Number.isFinite(t) && now > t + LIVE_WINDOW_MS;
};

/** Kicked off but still inside the live window. */
export const isInPlay = (m: { kickoffUtc: string }, now = Date.now()) => {
  const t = kickoffMs(m);
  return Number.isFinite(t) && now >= t && !isComplete(m, now);
};

export const isToday = (m: { kickoffUtc: string }) => {
  const t = kickoffMs(m);
  if (!Number.isFinite(t)) return false;
  const d = new Date(t);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
};

/** The lines that carry any signal at all, in render order. */
export const betList = (m: Match): BetLine[] => BET_LINES.map((k) => m.bets[k]);

export type BetOutcome = 'Pending' | 'Won' | 'Lost' | 'Void';

export interface PlacedBet {
  id: number;
  eventId: string;
  league: string;
  homeTeam: string;
  awayTeam: string;
  kickoffUtc: string;
  betName: string;
  requestedStake: number;
  shares: number;
  cost: number;
  avgPrice: number;
  bestPrice: number;
  limitPrice: number;
  isPartial: boolean;
  aiPercent: number | null;
  marketPercent: number | null;
  modelLeague: string | null;
  modelResults: number;
  signalEdge: number | null;
  signalZ: number | null;
  marketCount: number;
  status: string;
  outcome: BetOutcome;
  placedAtUtc: string;
}

/** Payout if this wins — each share settles at $1. */
export const potentialPayout = (b: PlacedBet) => b.shares;
export const potentialProfit = (b: PlacedBet) => b.shares - b.cost;

/** Settled P&L: +profit on a win, −cost on a loss, 0 if voided. Null while pending. */
export const realisedProfit = (b: PlacedBet): number | null => {
  switch (b.outcome) {
    case 'Won':
      return potentialProfit(b);
    case 'Lost':
      return -b.cost;
    case 'Void':
      return 0;
    default:
      return null;
  }
};

export const betEdge = (b: PlacedBet): number | null =>
  b.aiPercent !== null && b.marketPercent !== null ? b.aiPercent - b.marketPercent : null;

export const isLiveBet = (b: PlacedBet, now = Date.now()) => {
  const t = Date.parse(b.kickoffUtc);
  return Number.isFinite(t) && now >= t && now <= t + LIVE_WINDOW_MS;
};

// ─── formatting ──────────────────────────────────────────────────────────

export const pct = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${Math.round(v)}%`;

export const money = (v: number) => `$${v.toFixed(2)}`;

export const signed = (v: number | null, digits = 0) =>
  v === null ? '' : `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(digits)}`;

export function kickoffDisplay(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'TBD';
  const d = new Date(t);
  return d.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function relativeKickoff(iso: string, now = Date.now()): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const mins = Math.round((t - now) / 60000);
  if (mins < -180) return 'finished';
  if (mins < 0) return 'in play';
  if (mins < 60) return `in ${mins}m`;
  if (mins < 60 * 24) return `in ${Math.round(mins / 60)}h`;
  return `in ${Math.round(mins / (60 * 24))}d`;
}

/** One-line structured profile from the prediction. Empty when the model supplied no ratings. */
export function profileDisplay(p: Prediction | null): string {
  if (!p) return '';
  const hasRatings =
    p.homeAttack !== null || p.awayAttack !== null || p.homeDefense !== null || p.awayDefense !== null;
  if (!hasRatings) return '';

  const parts: string[] = [];
  if (p.homeAttack !== null || p.awayAttack !== null) {
    parts.push(`Attacks: ${p.homeAttack ?? '?'}/${p.awayAttack ?? '?'}`);
  }
  if (p.homeDefense !== null || p.awayDefense !== null) {
    parts.push(`Defenses: ${p.homeDefense ?? '?'}/${p.awayDefense ?? '?'}`);
  }
  if (p.keyAttackerMissing) parts.push('Key striker out');
  if (p.keyDefenderMissing) parts.push('Key defender out');
  if (p.openGameExpected) parts.push('Open game expected');
  return parts.join('  ·  ');
}
